/* Champion Truth Layer — Command Center
 *
 * WHY THIS FILE EXISTS
 * This code shipped for one day as a Netlify snippet injected into the HTML shell
 * of app.srchamp.com. That was a mistake and it cost real damage twice:
 *   1. It was installed on the wrong Netlify site (sr-championship-command-center,
 *      which has no custom domain) and "verified" there, while Brian's team saw
 *      nothing. Three rounds lost.
 *   2. Because a snippet is not reviewed, diffed or rolled back, it shipped a
 *      security defect: 61 provenance records were baked into a public asset and
 *      readable by any signed-out visitor of the login page, and the backing
 *      service answered /api/state to the whole internet.
 * Neither would have survived a pull request. Hence: in the repo.
 *
 * WHAT IT DOES
 * Decorates championship rows with a trust chip, and provides the rep-facing
 * panel: criteria checklist, Champions list, worklist, Find champion, Set-it-
 * myself, flag, undo. It holds NO business data — everything is fetched from
 * sr-champion-resync after the visitor proves a Southern Recognition session.
 *
 * TWO RULES FOR WHOEVER TOUCHES THIS NEXT
 * 1. The row hook is td[data-col="name"] — an unhashed attribute added by the
 *    Command Center lane in v1.38.0 for exactly this purpose. Do NOT go back to
 *    the CSS-module class (_tName_<hash>_70): that hash is derived from the
 *    stylesheet's CONTENT, so any edit to pursuit.module.css renames it and
 *    silently kills every chip. That is the mechanism behind the breakage above.
 * 2. Nothing may run before there is a session. No globals, no styles, no DOM,
 *    no network, and above all no provenance in page memory. See the auth gate
 *    at the bottom of this file.
 *
 * MIGRATION STATE — CORRECTED 2026-10-03, AND THE CORRECTION IS THE POINT.
 *
 * This block used to say the snippet and this module "guard on the same
 * window.__SR_TRUTH__ flag, so whichever loads first wins". Both halves were
 * true and together they were misleading, because WHICHEVER LOADS FIRST WAS
 * NEVER IN DOUBT: the snippet is a classic script at the end of <body> and runs
 * during parse; this module is type="module" and is deferred. The stale copy won
 * every page load, so production had been running code 95 lines behind the code
 * in this file for the whole migration, and nobody could see it in a diff.
 *
 * MEASURED, not assumed: the snippet is a strict SUBSET of this module on window
 * globals, DOM ids, CSS class names, service paths, tier keys and localStorage
 * keys — window.__srtOpen exists only here. Deleting the snippet loses nothing.
 * Re-run `node tools/compare-truth-layer.mjs` to check that again rather than
 * trusting this line.
 *
 * THE FIX IS IN THE REPO AND DOES NOT WAIT ON ANYONE. index.html claims
 * window.__SR_TRUTH__ in an inline head script, which runs before the end of the
 * body, so the snippet now returns at its first line and is inert. Ownership for
 * THIS module moved to window.__SR_TRUTH_OWNER__ so the head claim does not
 * disable it. Nothing here depends on the snippet being switched off.
 *
 * STILL WORTH DOING, WHEN SOMEONE HAS THE NETLIFY UI OPEN: delete it at Netlify
 * -> Project configuration -> Post processing -> Snippet injection. That removes
 * a 65 kB blocking request and a foreign origin from the shell. Until then the
 * foreign-origin guard in verify-live.mjs reports it on every run, by design —
 * a guard that goes quiet about a condition that is still true is how this was
 * reported as "already covered" when nothing was checking.
 */

import {provenanceFailure, provenanceHttpError} from './provenanceAvailability.js'
import {getToken} from '../../../lib/api.js'
import {getSessionGeneration, onSessionGenerationChange, sessionChangedError} from '../../../lib/sessionGeneration.js'

export default function initTruthLayer() {
  'use strict';
  /* ⚠️ THE GUARD IS `__SR_TRUTH_OWNER__`, NOT `__SR_TRUTH__`, AND THAT SWAP IS
     THE FIX — NOT A RENAME.

     `__SR_TRUTH__` is the flag BOTH copies used to mean "a truth layer is
     running". That made it a race, and this copy lost it on every page load:
     the injected snippet is a CLASSIC script at the end of <body> so it runs
     during parse, while this module is deferred and runs after. Production has
     been serving the stale copy for its entire life.

     index.html now claims `__SR_TRUTH__` in an inline head script, which runs
     earlier than anything in the body and so stops the snippet at its own first
     line. If this module still treated that flag as "somebody else owns it",
     the fix would disable the very copy it exists to promote. So ownership moved
     to a flag only THIS code sets, and `__SR_TRUTH__` is left to mean exactly
     what the snippet reads it as: stand down. */
  if (window.__SR_TRUTH_OWNER__) return;   // another copy of THIS module already took it

  var SVC = 'https://sr-champion-resync.netlify.app';
  var FEEDRE = /\/\.netlify\/functions\/championships/;
  var SEED = {};                       // filled from /api/provenance, authenticated only
  /* Session key and precedence follow src/lib/api.js. As of `4386ab0` the session
     lives in localStorage (shared across tabs) with a one-time fallback read of
     the legacy per-tab sessionStorage copy, which setToken then clears. Read in
     that same order so this layer can never disagree with the app about who is
     signed in. */
  function srTok() { return getToken() || ''; }

  var TIER = {
    VERIFIED_BY_US: { label:'Verified by us', dot:'#bbf7d0', bg:'#15803d', fg:'#ffffff',
      one:'A person here set or confirmed this. Locked — automation will never change it.' },
    OFFICIAL:      { label:'Official',      dot:'#bbf7d0', bg:'#16a34a', fg:'#ffffff',
      one:'Confirmed on the governing body’s own results page. Use it.' },
    CORROBORATED:  { label:'Corroborated',  dot:'#fde68a', bg:'#d97706', fg:'#ffffff',
      one:'Not on the governing body’s site, but a credible outlet or the winning school confirms it. Usually fine; glance at the source first.' },
    SINGLE_SOURCE: { label:'Single source', dot:'#fed7aa', bg:'#ea580c', fg:'#ffffff',
      one:'One source only, or only an aggregator. Check before you put it in front of a customer.' },
    UNVERIFIED:    { label:'Unverified',    dot:'#fecaca', bg:'#dc2626', fg:'#ffffff',
      one:'A champion is recorded but nobody knows where it came from. Do not quote it yet.' },
    DISPUTED:      { label:'Disputed',      dot:'#fecaca', bg:'#b91c1c', fg:'#ffffff',
      one:'Someone found this wrong. Do not use it until it is fixed.' },
    NEEDS_CHAMPION:{ label:'Needs champion',dot:'#e2e8f0', bg:'#64748b', fg:'#ffffff',
      one:'This event is over and no champion is recorded. It is on the resync worklist.' },
    /* THE TWO QUIET TIERS CARRY NO FILL, so their text colour is doing all the
       work and it is read against the PLATFORM'S CREAM PAGE (#f4efe3), not the
       dark panel this palette was originally drawn for. slate-400 (#94a3b8) on
       cream measures ~2.2:1 — below the 4.5:1 needed for 10px type, and that is
       before `.srt-quiet`'s opacity multiplier. Darkened to slate-600 /
       amber-800, which stay visually quiet (no fill, hairline border) while
       actually being legible. Measured on the real page, not assumed. */
    AMBIGUOUS:     { label:'Name not unique', dot:'#cbd5e1', bg:'transparent', fg:'#854d0e', quiet:1,
      one:'Several records share this exact name, so this row cannot be tied to one of them. The record needs a real championship name before it can be trusted.' },
    NO_CHAMPION_YET:{ label:'No champion yet', dot:'#cbd5e1', bg:'transparent', fg:'#475569', quiet:1,
      one:'Nothing to trust yet — this championship has not been decided, or has not been picked up as concluded.' }
  };

  /* Brian asked for "a percentage of certainty". A per-record percentage would be
     invented precision — nothing in the data supports 92% vs 96% on one championship.
     Instead: a 4-segment strength meter derived mechanically from the tier (so two
     people reading the same source get the same meter), and a REAL percentage at the
     portfolio level, which is a countable fact about the dataset. */
  var STRENGTH = { VERIFIED_BY_US:4, OFFICIAL:4, CORROBORATED:3, SINGLE_SOURCE:2,
                   UNVERIFIED:1, DISPUTED:0, NEEDS_CHAMPION:0, AMBIGUOUS:0, NO_CHAMPION_YET:0 };
  function meter(tier) {
    var n = STRENGTH[tier] || 0, cfg = TIER[tier] || TIER.NO_CHAMPION_YET, out = '';
    for (var i = 0; i < 4; i++)
      out += '<span class="srt-seg" style="background:' + (i < n ? (cfg.bg === 'transparent' ? cfg.fg : cfg.bg) : 'rgba(128,128,128,.25)') + '"></span>';
    return '<span class="srt-meter" title="' + (n) + ' of 4 — set by the kind of source, not by a guess">' + out + '</span>';
  }

  var S = { byName:{}, byId:{}, state:null, svcOk:null, provenanceError:null, stateError:null, me:'' };
  var started = false, panel = null, hooksInstalled = false, activeGeneration = -1;
  function active(ticket) { return started && ticket === activeGeneration && ticket === getSessionGeneration(); }

  /* ---------- 1. chain the fetch wrapper (do not clobber the display snippet) ---------- */
  var prior = window.fetch;
  function installHooks() {

  /* Recognise a championships payload by its SHAPE, not by its URL.
     The Command Center reads /.netlify/functions/championships; the Platform and
     Hub at app.srchamp.com may read api.srchamp.com/api/championships instead.
     Matching on URL meant the wrapper indexed nothing on those apps and no badge
     could ever paint. Shape-matching works on all three. */
  function championshipArray(j) {
    if (!j || typeof j !== 'object') return null;
    var cands = [];
    if (Array.isArray(j)) cands.push(j);
    else for (var k in j) { if (Array.isArray(j[k]) && j[k].length) cands.push(j[k]); }
    for (var c = 0; c < cands.length; c++) {
      var arr = cands[c], s0 = arr[0];
      if (!s0 || typeof s0 !== 'object') continue;
      var hasId   = ('id' in s0) || ('hs_object_id' in s0) || ('objectId' in s0) || ('recordId' in s0);
      var hasName = ('championship_name' in s0) || ('sr_display_name' in s0) || ('championshipName' in s0) ||
                    (('name' in s0) && (('sr_sport' in s0) || ('current_champion' in s0) ||
                                        ('sr_champion_status' in s0) || ('currentChampion' in s0) || ('sport' in s0)));
      if (hasId && hasName) return arr;
    }
    return null;
  }


  /* ---------- display-name disambiguation (ported to the Platform, 2026-08-20) ----------
     The 2026-08-18 snippet did this on the old Command Center only, matching on that
     app's feed URL, so Brian's team never had it. Ported here and made deterministic:
     the name a record gets depends only on that record, never on what else has loaded,
     so a row can never be renamed after it is drawn.
     Measured on the live object: 2,540 records shared a name -> 24. 10,385 distinct
     names -> 12,714 of 12,726. The 24 that remain are Track & Field pairs with no date
     at all, where every available qualifier is identical — genuinely indistinguishable,
     left alone rather than faked. */
  function srDisplayName(r) {
    var n = (r.championship_name || r.name || '').trim();
    if (!n) return n;
    var sp = (r.sr_sport || '').trim(), g = (r.sr_gender || '').trim(), low = n.toLowerCase();
    if (sp && low.indexOf(sp.toLowerCase()) < 0) { n += ' \u2014 ' + sp; low = n.toLowerCase(); }
    if (g && g !== 'Open' && low.indexOf('(' + g.toLowerCase() + ')') < 0) { n += ' (' + g + ')'; low = n.toLowerCase(); }
    var d = (r.sr_event_date || r.sr_projected_date || '').slice(0, 10);
    if (/track/i.test(sp) && d) { var m = +d.slice(5, 7); n += (m >= 12 || m <= 3) ? ' \u00b7 Indoor' : ' \u00b7 Outdoor'; low = n.toLowerCase(); }
    var lv = (r.sr_level || '').trim();
    if (lv && low.indexOf(lv.toLowerCase()) < 0) { n += ' \u00b7 ' + lv; low = n.toLowerCase(); }
    var st = (r.sr_state || r.sr_state_location || '').trim();
    if (st && n.indexOf(st) < 0) n += ' \u00b7 ' + st;
    return n;
  }

  function index(j) {
    if (!active(getSessionGeneration())) return;
    try {
      var arr = championshipArray(j); if (!arr) return;
      var added = 0;
      for (var i = 0; i < arr.length; i++) {
        var r = arr[i]; var id = String(r.id || r.hs_object_id || r.objectId || r.recordId || ''); if (!id) continue;
        /* Rewrite in place BEFORE the app reads the payload, so the rendered row and
           our index always agree on the same string. Guarded so a re-fetch cannot
           stack suffixes. */
        if (!r.__srtNamed) {
          var disp = srDisplayName(r);
          if (disp) { r.championship_name = disp; if ('name' in r) r.name = disp; r.sr_display_name = disp; }
          try { Object.defineProperty(r, '__srtNamed', { value: 1, enumerable: false }); } catch (e) { r.__srtNamed = 1; }
        }
        S.byId[id] = r; added++;
        [r.championship_name, r.championshipName, r.name, r.sr_display_name].forEach(function (n) {
          n = (n || '').trim(); if (!n) return;
          S.byName[n] = S.byName[n] || [];
          if (S.byName[n].indexOf(id) < 0) S.byName[n].push(id);
        });
      }
      if (added) { ensureLauncher(); clearTimeout(window.__srtP); window.__srtP = setTimeout(paint, 250); }
    } catch (e) {}
  }

  /* The 2026-08-18 display-name snippet (Command Center only) rewrites the feed by
     patching res.json(). We must patch json() ON TOP of theirs — cloning the
     Response would give the raw, un-rewritten names and nothing would match.
     Where that snippet is absent (Platform, Hub) this still works unchanged. */
  window.fetch = function () {
    var ticket = getSessionGeneration();
    var p = prior.apply(this, arguments);
    return p.then(function (res) {
      if (ticket !== getSessionGeneration()) throw sessionChangedError();
      try {
        if (res && typeof res.json === 'function' && !res.__srtWrapped) {
          res.__srtWrapped = 1;
          var orig = res.json.bind(res);
          res.json = function () { return orig().then(function (j) { if (ticket !== getSessionGeneration()) throw sessionChangedError(); index(j); return j; }); };
        }
      } catch (e) {}
      return res;
    });
  };

  /* Some SR surfaces read the feed over XMLHttpRequest rather than fetch. */
  try {
    var XO = XMLHttpRequest.prototype.open, XS = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var x = this, ticket = getSessionGeneration();
      x.addEventListener('load', function () {
        try {
          if (!active(ticket)) return;
          var ct = (x.getResponseHeader && x.getResponseHeader('content-type')) || '';
          if (!/json/i.test(ct)) return;
          var t = x.responseText; if (!t || t.length < 40 || t[0] !== '{' && t[0] !== '[') return;
          index(JSON.parse(t));
        } catch (e) {}
      });
      return XS.apply(this, arguments);
    };
  } catch (e) {}

  window.__srtProbe = function (txt) {
    var ids = S.byName[(txt || '').trim()];
    return { text: txt, indexed: !!ids, ids: ids || null, tier: ids && ids.length === 1 ? (tierFor(ids[0]) || 'NO_CHAMPION_YET') : null };
  };
  }

  function installDebug() {
  window.__srtDebug = function () {
    return { host: location.host, names: Object.keys(S.byName).length, ids: Object.keys(S.byId).length,
             provenance: Object.keys(SEED).length, svcOk: S.svcOk, tierCounts: (S.state && S.state.counts) || null,
             chips: document.querySelectorAll('.srt-chip[data-srt-id]').length,
             launcher: !!document.getElementById('srt-launch') };
  };
  }

  /* ---------- 2. service state ---------- */
  function authHeaders(extra) {
    var h = extra || {}; var t = srTok();
    if (t) h.authorization = 'Bearer ' + t;
    return h;
  }
  function loadProvenance() {
    var ticket = getSessionGeneration();
    return fetch(SVC + '/api/provenance', { cache:'no-store', headers: authHeaders() })
      .then(function (r) { if (!active(ticket)) throw sessionChangedError(); if (!r.ok) throw provenanceHttpError(r.status); return r.json(); })
      .then(function (j) { if (!active(ticket)) throw sessionChangedError(); SEED = j.provenance || {}; S.provenanceError = null; return SEED; });
  }
  /* If the rep's session ends while the page is open, everything must come down —
     otherwise provenance stays readable in a tab that is no longer signed in. */
  function teardown(reason) {
    try {
      SEED = {}; S = {byName: {}, byId: {}, state: null, svcOk: null, provenanceError:null, stateError:null, me: ''};
      clearTimeout(window.__srtP); clearTimeout(window.__srtT); _pending = [];
      bodyEl = null; tabsEl = null; ctxId = null; view = 'summary';
      var kill = document.querySelectorAll('#srt-launch,#srt-panel,.srt-chip[data-srt-id],.srt-chip[data-srt-ambiguous]');
      for (var i = 0; i < kill.length; i++) kill[i].remove();
      panel = null; started = false; activeGeneration = -1;
      try { delete window.__SR_TRUTH__; delete window.__srtDebug; delete window.__srtProbe;
            delete window.__srtRepaint; delete window.__srtRepaintLocal; delete window.__srtOpen; } catch (e) {}
      if (window.console && console.info) console.info('[SR truth layer] stood down: ' + reason);
      // The shared session-generation subscription owns subsequent login.
    } catch (e) {}
  }

  function loadState(force) {
    var ticket = getSessionGeneration();
    return fetch(SVC + '/api/state' + (force ? '?refresh=1' : ''), { cache:'no-store', headers: authHeaders() })
      .then(function (r) { if (!active(ticket)) throw sessionChangedError(); if (r.status === 401) { teardown('session ended'); throw provenanceHttpError(401); } if (!r.ok) throw provenanceHttpError(r.status); return r.json(); })
      .then(function (j) { if (!active(ticket)) throw sessionChangedError(); S.state = j; S.svcOk = true; S.stateError = null; repaint(); if (panel) render(); return j; })
      .catch(function (e) { if (!active(ticket)) throw sessionChangedError(); S.svcOk = false; S.stateError = provenanceFailure(e.status); paint(); if (panel) render(); });
  }

  function tierFor(id) {
    var st = S.state;
    if (st) {
      if (st.overrides && st.overrides[id]) return 'VERIFIED_BY_US';
      if (st.flags && st.flags[id]) return 'DISPUTED';
      if (st.tiers && st.tiers[id]) return st.tiers[id];
    }
    if (SEED[id]) return SEED[id].tier;
    var r = S.byId[id];
    if (r && r.current_champion && String(r.current_champion).trim()) return 'UNVERIFIED';
    return null;
  }
  function infoFor(id) {
    var st = S.state || {};
    var ov = st.overrides && st.overrides[id];
    var fl = st.flags && st.flags[id];
    var pr = st.proposals && st.proposals[id];
    var sd = SEED[id];
    var r = S.byId[id] || {};
    var champ = ov ? ov.champion : (r.current_champion || '');
    var src = ov ? ov.source_url : (sd ? sd.src : (pr ? pr.source_url : ''));
    var at  = ov ? ov.at.slice(0,10) : (sd ? sd.at : (pr ? pr.detected_at : ''));
    var wl = (st.worklist||[]).filter(function(w){return w.id===id;})[0];
    return { tier: tierFor(id), champ: champ, src: src, at: at, ov: ov, fl: fl, pr: pr,
             note: sd ? sd.note : '', rec: r, lastSynced: wl ? wl.lastSynced : null,
             lastResult: wl ? wl.lastResult : null };
  }

  /* ---------- 3. styles ---------- */
  function installStyles() {
  var css = document.createElement('style');
  css.textContent = [
   '.srt-chip{display:inline-flex;align-items:center;gap:4px;margin-left:8px;padding:1px 7px;border-radius:999px;',
   'font-size:10px;font-weight:800;letter-spacing:.03em;vertical-align:middle;cursor:pointer;white-space:nowrap;border:1px solid rgba(255,255,255,.10)}',
   '.srt-dot{width:6px;height:6px;border-radius:50%;display:inline-block}',
   '.srt-lock{font-size:var(--fs-xs);opacity:.9}',
   '.srt-meter{display:inline-flex;gap:2px;vertical-align:middle;margin-left:6px}',
   '.srt-check{display:flex;gap:9px;align-items:flex-start;padding:8px 10px;border:1px solid rgba(255,255,255,.07);',
   'border-radius:10px;margin-bottom:6px;background:rgba(255,255,255,.02)}',
   '.srt-mark{font-size:15px;line-height:1.25;flex:0 0 auto;font-weight:800}',
   '.srt-clabel{font-weight:700;font-size:13px}',
   '.srt-clabel.srt-cfail{opacity:.85}',
   '.srt-fix{margin-top:4px;font-size:var(--fs-xs);color:#fdba74}',
   '.srt-seg{width:9px;height:7px;border-radius:2px;display:inline-block}',
   /* No `opacity` here: a quiet chip is quiet because it has no FILL, not
      because it is faded. Multiplying an already-low-contrast colour by .75
      against the cream page is what made these unreadable. */
   '.srt-chip.srt-quiet{font-weight:700;border:1px solid currentColor;background:transparent}',
   '#srt-launch{position:fixed;right:18px;bottom:18px;z-index:2147483000;background:#f0b429;color:#101418;border:0;',
   'border-radius:999px;padding:11px 17px;font:800 13px system-ui;box-shadow:0 8px 26px rgba(0,0,0,.5);cursor:pointer}',
   /* 🔴 THIS IS THE SECOND PANEL BRIAN SAW BESIDE HIS RECORD.
    *
    * A fixed overlay at `width: min(560px, 100vw)` and `z-index: 2147483001`
    * — above everything, including the record drawer. On iOS Safari a page
    * whose layout viewport is wider than the visual one lays fixed elements
    * out against the LAYOUT viewport, so this panel and the drawer end up at
    * different offsets and paint beside each other. That is exactly the
    * "two surfaces at once" in the screenshot, and the drawer's own `100vw`
    * was only half of it.
    *
    * `100%` of a fixed, `right: 0` box is the viewport on every engine. */
   '#srt-panel{position:fixed;right:0;top:0;height:100dvh;width:100%;max-width:560px;z-index:2147483001;background:#0c1016;',
   'color:#e6e9ef;font:13px/1.55 system-ui;box-shadow:-14px 0 46px rgba(0,0,0,.6);display:none;flex-direction:column;border-left:1px solid rgba(255,255,255,.08)}',
   '#srt-panel.open{display:flex}',
   '.srt-hd{padding:13px 16px;border-bottom:1px solid rgba(255,255,255,.08);display:flex;align-items:center;gap:10px}',
   '.srt-tabs{display:flex;gap:6px;padding:9px 16px;border-bottom:1px solid rgba(255,255,255,.08);flex-wrap:wrap}',
   '.srt-tab{background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.08);color:#cbd5e1;border-radius:999px;',
   'padding:5px 11px;font-size:var(--fs-xs);font-weight:700;cursor:pointer}',
   '.srt-tab.on{background:#f0b429;color:#101418;border-color:#f0b429}',
   '.srt-body{padding:14px 16px;overflow:auto;flex:1}',
   '.srt-row{padding:9px 10px;border:1px solid rgba(255,255,255,.07);border-radius:10px;margin-bottom:7px;background:rgba(255,255,255,.02)}',
   '.srt-row b{font-weight:700}',
   '.srt-mut{color:#94a3b8;font-size:var(--fs-xs)}',
   '.srt-btn{background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.12);color:#e6e9ef;border-radius:8px;',
   'padding:5px 10px;font-size:var(--fs-xs);font-weight:700;cursor:pointer;margin-right:6px;margin-top:6px}',
   '.srt-btn.pri{background:#f0b429;color:#101418;border-color:#f0b429}',
   '.srt-btn:disabled{opacity:.45;cursor:not-allowed}',
   '.srt-in{width:100%;box-sizing:border-box;background:#141a22;border:1px solid rgba(255,255,255,.12);color:#e6e9ef;',
   'border-radius:8px;padding:7px 9px;font:13px system-ui;margin-top:5px}',
   '.srt-warn{background:rgba(234,179,8,.10);border:1px solid rgba(234,179,8,.30);color:#fde047;border-radius:9px;padding:9px 11px;margin-bottom:11px;font-size:var(--fs-xs)}',
   '.srt-ok{background:rgba(34,197,94,.10);border:1px solid rgba(34,197,94,.30);color:#86efac;border-radius:9px;padding:9px 11px;margin:9px 0;font-size:var(--fs-xs)}',
   '.srt-stat{display:inline-block;min-width:96px;margin:0 10px 10px 0}',
   '.srt-stat b{display:block;font-size:19px}',
   '@media (max-width:640px){',
   '  .srt-chip{display:inline-flex;margin-left:6px;font-size:var(--fs-xs);padding:1px 6px}',
   '  #srt-panel{width:100%;max-width:none;left:0;right:0}',
   '  #srt-launch{right:12px;bottom:12px;padding:13px 18px;font-size:14px;border-radius:999px}',
   '  .srt-tabs{gap:5px;padding:8px 12px}',
   '  .srt-tab{padding:6px 10px;font-size:12px}',
   '  .srt-body{padding:12px}',
   '  .srt-stat{min-width:74px}',
   '}',
   '.srt-x{margin-left:auto;background:none;border:0;color:#94a3b8;font-size:20px;cursor:pointer}'
  ].join('');
  document.head.appendChild(css);
  }

  /* ---------- 4. badge painting on rows ---------- */
  var ROW = 'div.group.cursor-pointer.rounded-xl';
  var PAINTV = 1;
  /* Badge every element whose exact text is a championship name. Works across all
     Command Center views (list rows, the Action Needed cards, search results),
     because it keys off the rendered championship_name rather than one layout. */
  /* PRIMARY HOOK: td[data-col="name"] — an unhashed attribute the Command Center
     lane added in v1.38.0 precisely for this painter. The old target was the
     CSS-module class _tName_gb7na_70, whose hash is derived from the STYLESHEET'S
     CONTENT: any edit to pursuit.module.css, even one nowhere near .tName, renames
     it and silently stops every chip. That mechanism cost three rounds. The class
     stays in the selector only as a fallback for builds older than v1.38.0. */
  var SEL = 'td[data-col="name"],td[class*="_tName_"],div,span,p,a,h1,h2,h3,h4,li,b,strong';
  /* (a second `var PAINTV = 1` sat here — a leftover from the edit that
     inserted the selector above. Harmless as `var`, a real bug the moment
     anyone converts this file to let/const.) */

  /* An element's OWN text — direct text-node children only.
     The Platform's Command Center renders the name as
       <td class="_tName_"><span class="_sportAccent_"></span>Championship name</td>
     so the cell is NOT a leaf. The original leaf-only rule skipped it and no row
     was ever decorated. Matching on own text handles both that markup and the
     plain leaf nodes in the older Command Center app. */
  function ownText(el) {
    var kids = el.childNodes, s = '', n;
    for (var i = 0; i < kids.length; i++) { n = kids[i]; if (n.nodeType === 3) s += n.nodeValue; }
    return s.trim();
  }

  function chipFor(id, tier) {
    var cfg = TIER[tier];
    var chip = document.createElement('span');
    chip.className = 'srt-chip' + (cfg.quiet ? ' srt-quiet' : '');
    chip.style.background = cfg.bg; chip.style.color = cfg.fg;
    chip.title = cfg.one + '  (click for detail)';
    chip.innerHTML = '<span class="srt-dot" style="background:' + cfg.dot + '"></span>' + cfg.label +
      (tier === 'VERIFIED_BY_US' ? ' <span class="srt-lock">&#128274;</span>' : '');
    if (id) {
      chip.setAttribute('data-srt-id', id);
      chip.addEventListener('click', function (ev) {
        ev.stopPropagation(); ev.preventDefault();
        open('record', this.getAttribute('data-srt-id'));
      }, true);
    } else {
      chip.setAttribute('data-srt-ambiguous', '1');
    }
    return chip;
  }

  /* Several records can share a name (conference rows in particular). Rather than
     skip them, score each candidate against the rest of the rendered row — sport,
     level, state, year. Only accept a single strict winner; otherwise still skip,
     because a wrong badge is worse than no badge. */
  function disambiguate(ids, cell) {
    try {
      var row = cell.closest ? (cell.closest('tr') || cell.parentElement) : null;
      if (!row) return null;
      var hay = ' ' + (row.innerText || '').replace(/\s+/g, ' ').toLowerCase() + ' ';
      var best = -1, bestId = null, tie = false;
      for (var i = 0; i < ids.length; i++) {
        var r = S.byId[ids[i]]; if (!r) continue;
        var bits = [r.sr_sport || r.sport, r.sr_level || r.level, r.sr_gender || r.gender,
                    r.sr_state || r.sr_state_location || r.state,
                    (r.sr_event_date || r.event_date || '').slice(0, 4)];
        var score = 0;
        for (var b = 0; b < bits.length; b++) {
          var v = (bits[b] || '').toString().trim().toLowerCase();
          if (v.length > 1 && hay.indexOf(v) >= 0) score++;
        }
        if (score > best) { best = score; bestId = ids[i]; tie = false; }
        else if (score === best) tie = true;
      }
      return (!tie && best > 0) ? bestId : null;
    } catch (e) { return null; }
  }

  /* ---- ROW DECORATION STOOD DOWN, 2026-08-25 ---------------------------
   * Brian: "the champion trust button is an additional step, we should have
   * the trust in the actual column."
   *
   * Trust is now rendered by ChampionCell inside the CHAMPION column, derived
   * from the record itself (`champion_source_url`, `champion_provenance`) with
   * no second request. That made this layer's row chip redundant — and worse
   * than redundant, because the two DISAGREED IN PRODUCTION.
   *
   * MEASURED ON THE LIVE PAGE, not assumed: `__srtDebug()` returned
   * `svcOk: null, tierCounts: null` while painting 564 chips. The overlay
   * service never answered, so every chip fell through `tierFor(id) ||
   * 'NO_CHAMPION_YET'` to a DEFAULT. The PDGA Disc Golf row showed the winner
   * Gannon Buhr as "Unverified" here while the record carried a pdga.com
   * source URL that the column rendered as "Sourced" one cell to the right.
   *
   * TWO TRUST SYSTEMS CONTRADICTING EACH OTHER ON ONE ROW IS WORSE THAN
   * NEITHER: a rep who sees them disagree stops believing both, and the
   * cheaper thing to disbelieve is the one that says "verified".
   *
   * The panel, the criteria checklist and the proposal-review flow below are
   * untouched and still correct — it is only the unsourced ROW CHIP and the
   * floating launcher that are withdrawn. Set PAINT_ROW_CHIPS back to true if
   * the overlay service is ever made authoritative again.
   * -------------------------------------------------------------------- */
  var PAINT_ROW_CHIPS = false;

  function paint(root) {
    if (!started || !PAINT_ROW_CHIPS) return;
    try {
      var scope = root && root.querySelectorAll ? root : document;
      var els = scope.querySelectorAll(SEL);
      if (root && root.nodeType === 1) els = [root].concat(Array.prototype.slice.call(els));
      for (var i = 0; i < els.length; i++) {
        var n = els[i];
        if (n.__srtv === PAINTV) continue;
        if (n.closest && n.closest('#srt-panel')) continue;
        var t = n.children.length ? ownText(n) : (n.textContent || '').trim();
        if (t.length < 4 || t.length > 190) continue;
        var ids = S.byName[t]; if (!ids) continue;
        n.__srtv = PAINTV;
        if (n.querySelector && n.querySelector('.srt-chip')) continue;
        var id = ids.length === 1 ? ids[0] : disambiguate(ids, n);
        if (!id) {
          /* Several records share this exact name and the row gives us nothing to
             separate them. Say so, rather than leaving the row silently bare —
             these are the unnamed conference records and the team should see them. */
          var amb = chipFor(null, 'AMBIGUOUS');
          amb.title = ids.length + ' records share this exact name, so this row cannot be tied to one of ' +
                      'them. The record needs a real championship name before it can be trusted.';
          n.appendChild(amb);
          continue;
        }
        /* Every indexed championship gets a state. A row with nothing to trust yet
           says so quietly, rather than looking like the feature failed to load. */
        var tier = tierFor(id) || 'NO_CHAMPION_YET';
        n.appendChild(chipFor(id, tier));
      }
    } catch (e) {}
  }

  function repaint() { PAINTV++; 
    var old = document.querySelectorAll('.srt-chip[data-srt-id],.srt-chip[data-srt-ambiguous]');
    for (var i = 0; i < old.length; i++) old[i].remove();
    paint(); }
  function installRepaint() {
    window.__srtRepaint = function () { return loadState(true).then(repaint).catch(repaint); };
    window.__srtRepaintLocal = repaint;
  }

  var _pending = [];
  function _flush() {
    var list = _pending; _pending = [];
    for (var i = 0; i < list.length && i < 400; i++) paint(list[i]);
  }
  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var a = muts[i].addedNodes;
      for (var j = 0; j < a.length; j++) if (a[j].nodeType === 1 && !a[j].closest('#srt-panel')) _pending.push(a[j]);
    }
    if (!_pending.length) return;
    clearTimeout(window.__srtT); window.__srtT = setTimeout(_flush, 500);
  }).observe(document.body || document.documentElement, { childList: true, subtree: true });

  /* ---------- 5. panel ---------- */
  var bodyEl, tabsEl, view = 'summary', ctxId = null;
  /* Only surface the launcher once we have actually seen championship records, so
     it never appears on a login screen or an unrelated page of the app. */
  /* The floating "Champion trust" button is the "additional step" Brian named.
     It is withdrawn for the same reason as the row chip above; the panel is
     still reachable programmatically via window.__srtOpen(id) so nothing that
     was built here is lost. */
  function ensureLauncher() {
    if (!started || !PAINT_ROW_CHIPS) return;
    if (document.getElementById('srt-launch') || !document.body) return;
    var b = document.createElement('button');
    b.id = 'srt-launch'; b.textContent = 'Champion trust';
    b.onclick = function () { open('summary'); };
    document.body.appendChild(b);
  }
  function build() {
    ensureLauncher();
    if (panel) return;
    panel = document.createElement('div'); panel.id = 'srt-panel';
    panel.innerHTML =
      '<div class="srt-hd"><b>Champion Truth Layer</b>' +
      '<span class="srt-mut" id="srt-sync"></span>' +
      '<button class="srt-x" id="srt-close">&times;</button></div>' +
      '<div class="srt-tabs" id="srt-tabs"></div>' +
      '<div class="srt-body" id="srt-body"></div>';
    document.body.appendChild(panel);
    bodyEl = panel.querySelector('#srt-body'); tabsEl = panel.querySelector('#srt-tabs');
    panel.querySelector('#srt-close').onclick = function () { panel.classList.remove('open'); };
  }
  function open(v, id) { if (!active(getSessionGeneration())) return; view = v; ctxId = id || ctxId; if (!panel) build(); panel.classList.add('open'); render(); }

  /* The panel outlived its launcher. ChampionCell's trust chip in the CHAMPION
     column now opens it, so the detail is one click from the winner it is
     about rather than from a floating button in the corner that described the
     whole portfolio. Named on window because the column is React and this
     layer is not — this is the seam between them. */
  window.__srtOpen = function (id) { open(id ? 'record' : 'summary', id || null); };

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
    return ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' })[c]; }); }

  function banner() {
    if (S.provenanceError)
      return '<div class="srt-warn" role="status"><b>Provenance unavailable.</b> ' + esc(S.provenanceError) +
             ' No current provenance was loaded; this is not a verified or empty result.</div>';
    if (S.svcOk === false)
      return '<div class="srt-warn" role="status"><b>Live trust state unavailable.</b> ' + esc(S.stateError || provenanceFailure()) +
             ' Previously loaded provenance is not a current live-state confirmation. Resync and the live worklist are unavailable.</div>';
    var st = S.state; if (!st) return '';
    if (!st.writeEnabled) {
      if (nativeWriteAvailable())
        return '<div class="srt-warn"><b>Champions you set here DO save into HubSpot.</b> The source link, ' +
               'your note and the lock are held in this service, because those fields are not writable yet. ' +
               'So the champion sticks; the paperwork behind it is queued. ' +
               '<span class="srt-mut">(Automation still never overwrites anything you set.)</span></div>';
      return '<div class="srt-warn"><b>Saving to HubSpot is not switched on yet.</b> Everything you do here is ' +
             'saved and shows up in this panel immediately, and it is queued to write into HubSpot the moment ' +
             'data access is granted. Nothing you type is lost. <span class="srt-mut">(Reason: the HubSpot ' +
             'connection has not been re-approved for the championships object.)</span></div>';
    }
    return '';
  }

  function tabs() {
    var st = S.state || {};
    var champCount = 0;
    for (var kk in (st.tiers || {})) { var tv = st.tiers[kk];
      if (tv === 'OFFICIAL' || tv === 'CORROBORATED' || tv === 'SINGLE_SOURCE' || tv === 'UNVERIFIED' ||
          tv === 'DISPUTED' || tv === 'VERIFIED_BY_US') champCount++; }
    var defs = [['summary','Trust'],['champs','Champions (' + champCount + ')'],
                ['work','Needs a champion (' + (st.worklistCount != null ? st.worklistCount : '–') + ')'],
                ['conflict','Conflicts'],['help','What the labels mean']];
    if (ctxId) defs.splice(1, 0, ['record','This record']);
    tabsEl.innerHTML = defs.map(function (d) {
      return '<button class="srt-tab' + (view === d[0] ? ' on' : '') + '" data-v="' + d[0] + '">' + d[1] + '</button>';
    }).join('');
    Array.prototype.forEach.call(tabsEl.children, function (b) {
      b.onclick = function () { view = b.getAttribute('data-v'); render(); };
    });
    var sy = panel.querySelector('#srt-sync');
    sy.textContent = st.generatedAt ? ('last checked ' + new Date(st.generatedAt).toLocaleString()) : '';
  }

  function render() {
    if (!active(getSessionGeneration()) || !panel || !bodyEl) return;
    var st = S.state || {}, h = banner();
    // A failed read is neither a zero census nor permission to act on stale state.
    if (S.provenanceError || S.svcOk !== true) {
      tabsEl.innerHTML = ''; panel.querySelector('#srt-sync').textContent = '';
    }
    if (S.provenanceError || S.svcOk === false) { bodyEl.innerHTML = h; return; }
    if (S.svcOk !== true) {
      bodyEl.innerHTML = '<div class="srt-warn" role="status">Reading provenance and live trust state. Counts and actions are not available until this read completes.</div>';
      return;
    }
    tabs();
    if (view === 'summary') {
      var c = st.counts || {};
      h += '<div style="margin-bottom:6px">' + ['VERIFIED_BY_US','OFFICIAL','CORROBORATED','SINGLE_SOURCE','UNVERIFIED','DISPUTED','NEEDS_CHAMPION']
        .filter(function (k) { return c[k]; })
        .map(function (k) { return '<span class="srt-stat"><b style="color:' + TIER[k].dot + '">' + c[k] + '</b>' +
          '<span class="srt-mut">' + TIER[k].label + '</span></span>'; }).join('') + '</div>';
      var withChamp = (c.OFFICIAL||0)+(c.CORROBORATED||0)+(c.SINGLE_SOURCE||0)+(c.UNVERIFIED||0)+(c.DISPUTED||0)+(c.VERIFIED_BY_US||0);
      var solid = (c.OFFICIAL||0)+(c.VERIFIED_BY_US||0);
      var pct = withChamp ? Math.round(solid / withChamp * 100) : 0;
      h += '<div class="srt-row" style="border-color:rgba(34,197,94,.35)"><b style="font-size:22px;color:#22c55e">' + pct + '%</b>' +
           '<div>of the ' + withChamp + ' champions on file are backed by the governing body\'s own results page, ' +
           'or were set by someone here.</div>' +
           '<div class="srt-mut" style="margin-top:5px">This is a count, not an estimate: ' + solid + ' of ' + withChamp + '. ' +
           'There is deliberately no per-championship percentage — nothing in the data would justify one. ' +
           'Each record shows a 4-bar strength instead, set by the kind of source behind it.</div></div>';
      h += '<div class="srt-mut">Out of ' + (st.universe || '–') + ' championships, ' +
        ((c.OFFICIAL||0)+(c.CORROBORATED||0)+(c.UNVERIFIED||0)+(c.DISPUTED||0)+(c.VERIFIED_BY_US||0)) +
        ' currently hold a champion. Everything else has not happened yet or is on the worklist.</div>';
      h += '<div class="srt-row" style="margin-top:12px"><b>Provenance &rarr; HubSpot</b>' +
        '<div class="srt-mut" style="margin-top:3px">The 61 researched grades live in this service ' +
        'because the champion_* fields used to be unwritable. They are writable now ' +
        '(operational tier, commit 8db44cb). This writes them onto the records so the ' +
        'grade lives with the data and the scheduled job can re-check a source that rots.</div>' +
        '<button class="srt-btn pri" id="srt-migrate">Write the 61 grades into HubSpot</button>' +
        '<div id="srt-migrate-out"></div></div>';
      h += '<div style="margin-top:14px"><button class="srt-btn pri" id="srt-refresh">Refresh</button>' +
           '<button class="srt-btn" id="srt-runall">Find champions for everything on the worklist</button></div>';
    }
    else if (view === 'work') {
      var wl = st.worklist || [];
      h += '<div class="srt-mut" style="margin-bottom:9px">' + wl.length + ' events have finished with no champion recorded. ' +
           'These are the ones worth chasing. Sorted by how long they have sat.</div>';
      var LIM = S.wlLimit || 60;
      h += wl.slice(0, LIM).map(function (w) {
        var ls = w.lastSynced ? ('Last looked ' + new Date(w.lastSynced).toLocaleDateString() + ' — ' + esc(w.lastResult || ''))
                              : '<span style="color:#fca5a5">Never looked</span>';
        var pr = w.proposal ? ('<div class="srt-ok">Found: <b>' + esc(w.proposal.champion) + '</b> — ' +
                 '<a href="' + esc(w.proposal.source_url) + '" target="_blank" rel="noopener" style="color:#86efac">source</a>' +
                 ' &middot; waiting to be written</div>') : '';
        return '<div class="srt-row"><b>' + esc(w.name) + '</b><div class="srt-mut">' +
          esc([w.sport, w.state, w.body].filter(Boolean).join(' · ')) + (w.date ? ' · ' + w.date : '') + '</div>' +
          '<div class="srt-mut">' + ls + '</div>' + pr +
          '<button class="srt-btn pri" data-find="' + w.id + '">Find champion</button>' +
          '<button class="srt-btn" data-set="' + w.id + '">I know it</button></div>';
      }).join('') || '<div class="srt-mut">Nothing is waiting. Every concluded event has a champion.</div>';
      if (wl.length > LIM) h += '<button class="srt-btn" id="srt-more">Show ' + Math.min(60, wl.length - LIM) + ' more (' + (wl.length - LIM) + ' left)</button>';
    }
    else if (view === 'champs') {
      /* Every record that actually holds a champion, with its grade and its source.
         On a phone the Command Center table is 1,103px wide in a horizontally
         scrolling wrapper, so this list — not the table — is the usable surface. */
      var rows = [];
      for (var id in (st.tiers || {})) {
        var tv = st.tiers[id];
        if (['OFFICIAL','CORROBORATED','SINGLE_SOURCE','UNVERIFIED','DISPUTED','VERIFIED_BY_US'].indexOf(tv) < 0) continue;
        var inf = infoFor(id);
        rows.push({ id: id, tier: tv, champ: inf.champ,
                    name: (S.byId[id] && (S.byId[id].championship_name || S.byId[id].name)) || ('record ' + id),
                    src: inf.src, at: inf.at });
      }
      var order = ['VERIFIED_BY_US','DISPUTED','UNVERIFIED','SINGLE_SOURCE','CORROBORATED','OFFICIAL'];
      rows.sort(function (a, b) { return order.indexOf(a.tier) - order.indexOf(b.tier); });
      h += '<div class="srt-mut" style="margin-bottom:9px">' + rows.length + ' championships have a champion recorded. ' +
           'Least trustworthy first, so anything that needs attention is at the top.</div>';
      h += rows.map(function (r) {
        var cfg = TIER[r.tier];
        var cr = criteria(r.id), met = 0, tot = 0;
        cr.checks.forEach(function (c) { if (c.invert) return; tot++; if (c.pass) met++; });
        return '<div class="srt-row"><b>' + esc(r.name) + '</b>' +
          '<div style="font-size:15px;margin-top:3px">&#9819; <b>' + esc(r.champ || '—') + '</b></div>' +
          '<div style="margin-top:5px"><span class="srt-chip" style="margin-left:0;background:' + cfg.bg + ';color:' + cfg.fg + '">' +
          '<span class="srt-dot" style="background:' + cfg.dot + '"></span>' + cfg.label + '</span>' + meter(r.tier) +
          (r.at ? ' <span class="srt-mut">' + esc(r.at) + '</span>' : '') + '</div>' +
          '<div class="srt-mut" style="margin-top:4px">Criteria met <b>' + met + ' of ' + tot + '</b> &middot; ' +
          cr.sourceCount + ' source' + (cr.sourceCount === 1 ? '' : 's') + ' on file</div>' +
          (r.src ? '<div style="margin-top:5px"><a href="' + esc(r.src) + '" target="_blank" rel="noopener" style="color:#f0b429;font-weight:700">Open the source &#8599;</a></div>' : '') +
          '<button class="srt-btn" data-open="' + r.id + '">See the checks</button></div>';
      }).join('') || '<div class="srt-mut">No champions recorded yet.</div>';
    }
    else if (view === 'record') { h += recordView(); }
    else if (view === 'conflict') {
      h += '<div class="srt-mut" style="margin-bottom:9px">Where the detector found a different champion than the one on ' +
           'the record. These are the only things that get escalated — everything else stays quiet.</div>';
      var cf = st.conflicts || {};
      var keys = Object.keys(cf);
      h += keys.length ? keys.map(function (k) { var c = cf[k];
        return '<div class="srt-row"><b>' + esc(c.name) + '</b><div>On the record: <b>' + esc(c.stored) + '</b></div>' +
          '<div>Detector found: <b>' + esc(c.detected) + '</b> ' +
          '<a href="' + esc(c.source_url) + '" target="_blank" rel="noopener" style="color:#f0b429">source</a></div>' +
          '<button class="srt-btn" data-set="' + k + '">Decide this</button></div>'; }).join('')
        : '<div class="srt-mut">No conflicts.</div>';
    }
    else {
      h += Object.keys(TIER).map(function (k) {
        return '<div class="srt-row"><span class="srt-chip" style="margin-left:0;background:' + TIER[k].bg + ';color:' + TIER[k].fg + '">' +
          '<span class="srt-dot" style="background:' + TIER[k].dot + '"></span>' + TIER[k].label + '</span>' + meter(k) +
          '<div style="margin-top:5px">' + TIER[k].one + '</div></div>';
      }).join('') +
      '<div class="srt-row srt-mut">A label is decided by <b>what kind of source backs it</b>, not by a score. ' +
      'Anyone can read the source and land on the same label.</div>';
    }
    bodyEl.innerHTML = h;
    wire();
  }


  /* ---------- CRITERIA MET ----------
     Brian: "Doesn't need to be a percentage but a criteria met."
     Every check below is MECHANICAL — a rep can open the record and the source and
     independently agree it passed or failed. No judgement, no soft language.
     The tier is DERIVED from these checks, so the chip is a summary of the checklist
     rather than a separate opinion. */
  function concludedClient(rec) {
    var t = new Date().toISOString().slice(0, 10);
    var d = (rec.sr_event_date || rec.sr_confirmed_date || rec.sr_projected_date || '').slice(0, 10);
    if (d) return { known: true, pass: d < t, detail: 'Event date ' + d };
    var y = parseInt(rec.sr_season_year, 10);
    if (!y) return { known: false, pass: false, detail: 'No event date and no season year on the record' };
    return { known: true, pass: t > (y + 1) + '-07-31',
             detail: 'No date; season ' + y + ' runs Aug ' + y + ' to Jul ' + (y + 1) };
  }
  function host(u) { try { return String(u).split('/')[2].replace(/^www\./, ''); } catch (e) { return ''; } }

  function criteria(id) {
    var i = infoFor(id), sd = SEED[id] || null, rec = S.byId[id] || {}, ov = i.ov, fl = i.fl;
    var verdict = sd ? (sd.verdict || '') : '';
    var srcs = []; if (i.src) srcs.push(i.src); if (sd && sd.src2) srcs.push(sd.src2);
    var hasChamp = !!(i.champ && String(i.champ).trim());
    var officialSrc = !!(sd && sd.tier === 'OFFICIAL');
    var conc = concludedClient(rec);
    var nameOk = hasChamp && verdict !== 'CHAMPION_CONTRADICTED' && verdict !== 'MISASSIGNED';
    var dateOk = hasChamp && verdict !== 'DATE_CONTRADICTED' && verdict !== 'DATE_MISSING';

    var C = [];
    C.push({ k:'champ', label:'A champion is recorded', pass:hasChamp,
      detail: hasChamp ? i.champ : 'No champion on the record',
      fix: 'Use Set / correct the champion, or run Resync.' });
    C.push({ k:'src', label:'A source is on file', pass:srcs.length>0,
      detail: srcs.length ? srcs.length + ' source' + (srcs.length>1?'s':'') + ' on file' : 'No source link',
      fix: 'Add the link to the published result.' });
    C.push({ k:'gov', label:"Source is the governing body's own results page", pass:officialSrc,
      detail: officialSrc ? host(i.src) : (srcs.length ? 'Source is ' + host(i.src) + ', not the sanctioning body' : 'No source to judge'),
      fix: "Find the sanctioning body's own results or bracket page — not a homepage, not an aggregator." });
    C.push({ k:'corr', label:'Independently corroborated (2 or more sources)', pass:srcs.length>=2,
      detail: srcs.length + ' independent source' + (srcs.length===1?'':'s') + ' recorded',
      fix: 'Add a second source from a different organisation.' });
    C.push({ k:'name', label:'Champion name matches the source', pass:nameOk,
      detail: !hasChamp ? 'No champion to check'
              : (nameOk ? 'Checked against the cited source'
                        : (sd && sd.note ? sd.note : 'The source does not support this champion')),
      fix: 'Correct the champion, or clear it if it belongs to another record.' });
    C.push({ k:'date', label:'Recorded date matches the source', pass:dateOk,
      detail: !hasChamp ? 'No champion to check'
              : (dateOk ? 'Date agrees with the cited source'
                        : (sd && sd.note ? sd.note : 'The source gives a different date')),
      fix: 'Update the event date to the date the title was decided.' });
    C.push({ k:'conc', label:'Event confirmed concluded', pass:conc.pass, detail:conc.detail,
      fix: 'Nothing to do until the event is played.' });
    C.push({ k:'human', label:'Confirmed by a person here', pass:!!ov,
      detail: ov ? ((ov.acceptedProposal ? 'Accepted by ' : 'Set by ') + ov.by + ' on ' + String(ov.at).slice(0,10) +
                    (ov.note ? ' — \u201c' + ov.note + '\u201d' : ''))
                 : 'Nobody here has confirmed it',
      fix: 'If you know it first-hand, use Set / correct the champion.' });
    if (fl) C.push({ k:'flag', label:'Reported wrong by a person here', pass:false, invert:1,
      detail: fl.by + (fl.reason ? ' — \u201c' + fl.reason + '\u201d' : ''),
      fix: 'Resolve it by setting the correct champion.' });

    /* tier, derived from the checks above */
    var tier = (ov && ov.acceptedProposal && ov.proposalTier) ? ov.proposalTier
             : ov ? 'VERIFIED_BY_US'
             : fl ? 'DISPUTED'
             : !hasChamp ? (conc.pass ? 'NEEDS_CHAMPION' : 'NO_CHAMPION_YET')
             : !nameOk ? 'DISPUTED'
             : srcs.length === 0 ? 'UNVERIFIED'
             : officialSrc ? 'OFFICIAL'
             : (srcs.length >= 2 ? 'CORROBORATED' : (sd ? sd.tier : 'SINGLE_SOURCE'));
    var why = (ov && ov.acceptedProposal) ? 'a person here checked the source and accepted it'
            : ov ? 'a person here set it'
            : fl ? 'a person here reported it wrong'
            : !hasChamp ? 'there is no champion on the record yet'
            : !nameOk ? 'the source does not support the champion'
            : srcs.length === 0 ? 'no source is on file'
            : officialSrc ? "the governing body's own results page is cited and the name matches"
            : (srcs.length >= 2 ? 'two independent sources agree, but not the governing body itself'
                                : 'one source is cited and it is not the governing body');
    return { checks: C, tier: tier, why: why, sourceCount: srcs.length, sources: srcs };
  }

  function renderChecks(id) {
    var r = criteria(id), cfg = TIER[r.tier] || TIER.NO_CHAMPION_YET;
    var met = 0, total = 0;
    r.checks.forEach(function (c) { if (c.invert) return; total++; if (c.pass) met++; });
    var h = '<div class="srt-row"><b>Criteria met &mdash; ' + met + ' of ' + total + '</b>' +
      '<div class="srt-mut" style="margin-top:3px">Reads <b style="color:' + (cfg.bg === 'transparent' ? cfg.fg : cfg.bg) + '">' +
      cfg.label + '</b> because ' + esc(r.why) + '.</div></div>';
    h += r.checks.map(function (c) {
      var ok = c.pass, mark = c.invert ? '&#9888;' : (ok ? '&#10003;' : '&#10007;');
      var col = c.invert ? '#b91c1c' : (ok ? '#16a34a' : '#b45309');
      return '<div class="srt-check"><span class="srt-mark" style="color:' + col + '">' + mark + '</span>' +
        '<div><div class="srt-clabel' + (ok ? '' : ' srt-cfail') + '">' + c.label + '</div>' +
        '<div class="srt-mut">' + esc(c.detail) + '</div>' +
        (!ok && c.fix ? '<div class="srt-fix">To satisfy this: ' + esc(c.fix) + '</div>' : '') +
        '</div></div>';
    }).join('');
    return h;
  }

  function recordView() {
    if (!ctxId) return '<div class="srt-mut">Pick a record first — click a coloured label next to any championship.</div>';
    var i = infoFor(ctxId), r = i.rec || {}, cfg = TIER[i.tier] || TIER.NEEDS_CHAMPION;
    var h = '<div class="srt-row"><b>' + esc(r.championship_name || '(record ' + ctxId + ')') + '</b>' +
      '<div class="srt-mut">' + esc([r.sr_sport, r.sr_level, r.sr_state || r.sr_state_location, r.sr_conference].filter(Boolean).join(' · ')) + '</div></div>';
    h += '<div class="srt-row"><div class="srt-mut">Champion</div><div style="font-size:16px"><b>' +
      (i.champ ? esc(i.champ) : '<span style="color:#94a3b8">none recorded</span>') + '</b></div>' +
      '<div style="margin-top:7px"><span class="srt-chip" style="margin-left:0;background:' + cfg.bg + ';color:' + cfg.fg + '">' +
      '<span class="srt-dot" style="background:' + cfg.dot + '"></span>' + cfg.label + '</span>' + meter(i.tier) + '</div>' +
      '<div class="srt-mut" style="margin-top:6px">' + cfg.one + '</div>' +
      (i.src ? '<div style="margin-top:7px"><a href="' + esc(i.src) + '" target="_blank" rel="noopener" style="color:#f0b429;font-weight:700">Open the source &#8599;</a></div>'
             : '<div class="srt-mut" style="margin-top:7px">No source on file.</div>') +
      (i.at ? '<div class="srt-mut">Champion dated ' + esc(i.at) + '</div>' : '') +
      (i.note ? '<div class="srt-mut" style="margin-top:5px">' + esc(i.note) + '</div>' : '') +
      '<div class="srt-mut" style="margin-top:5px">' +
        (i.lastSynced ? 'Last looked for a champion ' + new Date(i.lastSynced).toLocaleString() + ' — ' + esc(i.lastResult || '')
                      : 'Nobody has looked for a champion on this record yet.') + '</div>' +
      (i.ov ? '<div class="srt-ok">Set by <b>' + esc(i.ov.by) + '</b> on ' + new Date(i.ov.at).toLocaleDateString() +
              (i.ov.note ? ' — &ldquo;' + esc(i.ov.note) + '&rdquo;' : '') +
              (i.ov.previousValue ? '<div class="srt-mut">Previous value kept: ' + esc(i.ov.previousValue) + '</div>' : '') +
              '<div class="srt-mut">Locked. No automation can change this.</div></div>' : '') +
      (i.fl ? '<div class="srt-warn">Flagged wrong by <b>' + esc(i.fl.by) + '</b>' +
              (i.fl.reason ? ' — &ldquo;' + esc(i.fl.reason) + '&rdquo;' : '') + '</div>' : '') +
      '</div>';
    var st2 = S.state || {};
    var prop = (st2.proposals || {})[ctxId];
    var rej  = (st2.rejections || {})[ctxId];
    var noChamp = !(i.champ && String(i.champ).trim());

    if (prop) {
      /* A machine proposal is waiting. The rep decides — nobody above them. */
      var pcfg = TIER[prop.tier] || TIER.CORROBORATED;
      h += '<div class="srt-row" style="border-color:rgba(240,180,41,.45)">' +
        '<b>Proposed champion &mdash; your call</b>' +
        '<div style="font-size:17px;margin-top:5px">&#9819; <b>' + esc(prop.champion) + '</b></div>' +
        '<div style="margin-top:6px"><span class="srt-chip" style="margin-left:0;background:' + pcfg.bg + ';color:' + pcfg.fg + '">' +
        '<span class="srt-dot" style="background:' + pcfg.dot + '"></span>' + pcfg.label + '</span>' + meter(prop.tier) + '</div>' +
        (prop.source_url ? '<div style="margin-top:6px"><a href="' + esc(prop.source_url) + '" target="_blank" rel="noopener" style="color:#f0b429;font-weight:700">Check the source &#8599;</a></div>' : '') +
        '<div class="srt-mut" style="margin-top:5px">Found ' + esc(prop.detected_at || '') + ' by ' + esc(prop.adapter || 'detection') + '.</div>' +
        '<button class="srt-btn pri" data-accept="' + ctxId + '">Accept it</button>' +
        '<button class="srt-btn" data-set="' + ctxId + '">Edit it</button>' +
        '<button class="srt-btn" data-reject="' + ctxId + '">Reject it</button></div>';
    }

    h += '<div class="srt-row">' +
      (noChamp
        ? '<button class="srt-btn pri" data-find="' + ctxId + '">Find champion</button>'
        : '<button class="srt-btn" data-find="' + ctxId + '">Look again</button>') +
      '<button class="srt-btn' + (noChamp ? '' : ' pri') + '" data-set="' + ctxId + '">' +
        (noChamp ? 'I know it &mdash; set it myself' : 'Set / correct the champion') + '</button>' +
      (noChamp ? '' : '<button class="srt-btn" data-flag="' + ctxId + '">This is wrong</button>') +
      (i.ov ? '<button class="srt-btn" data-clear="' + ctxId + '">Undo my override</button>' : '') +
      '<div id="srt-find"></div>' +
      (rej ? '<div class="srt-mut" style="margin-top:6px">You rejected a suggestion here on ' +
             esc(String(rej.at).slice(0,10)) + '. The next scheduled run will look again.</div>' : '') +
      '</div>';

    h += renderChecks(ctxId);
    h += '<div id="srt-form"></div>';
    return h;
  }

  function who() {
    if (S.me) return S.me;
    var n = prompt('Your name (so the next person knows who set this):', '');
    if (n) { S.me = n.trim(); localStorage.setItem('sr_truth_me', S.me); }
    return S.me;
  }


  /* ---------- native write path (discovered 2026-08-20) ----------
     app.srchamp.com's own backend can write to 2-64425303 via
     POST https://api.srchamp.com/api/champion-update  {id, <field>:<value>}
     with the logged-in rep's Bearer token. It is attributed server-side ("by").
     Its allowlist is NARROW — verified by no-op probe:
       ACCEPTED  current_champion, sr_pursuit_status, sr_confirmed_date, sr_assigned_rep
       REJECTED  every champion_* provenance field, sr_champion_status, sr_crowned_at,
                 sr_winning_school, dates, sport/level/conference, championship_name
     ( /api/champion-write returns 200 for a bogus field AND an empty body, so its
       success is not evidence of anything. Do not use it. )
     So: the CHAMPION itself can be written today. The lock, tier, source and note
     live in the resync service until the allowlist or the connector is widened. */
  function srToken() { return srTok(); }
  function nativeWriteAvailable() { return /(^|\.)srchamp\.com$/.test(location.hostname) && !!srToken(); }
  function nativeSetChampion(id, champion) {
    var tok = srToken();
    if (!tok) return Promise.resolve({ ok: false, reason: 'no session token on this page' });
    return fetch('/api/champion-update', {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: 'Bearer ' + srTok() },
      body: JSON.stringify({ id: String(id), current_champion: champion })
    }).then(function (r) { return r.json().catch(function () { return {}; })
        .then(function (j) {
          if (r.status === 401) return { ok: false, kind: 'expired',
            reason: 'Your session has expired. Sign in again — nothing was written to HubSpot.' };
          return { ok: r.ok && !!j.ok, status: r.status, by: j.by, fields: j.fields,
                   kind: (r.ok && j.ok) ? 'written' : 'rejected',
                   reason: j.detail || j.error || ('HubSpot refused the write (status ' + r.status + ').') };
        }); })
      .catch(function (e) { return { ok: false, kind: 'network',
        reason: 'Could not reach HubSpot (' + String(e && e.message || e) + '). Nothing was written.' }; });
  }

  function post(path, body) {
    return fetch(SVC + '/api/' + path, { method:'POST',
      headers: authHeaders({ 'content-type':'application/json' }),
      body: JSON.stringify(body) }).then(function (r) { return r.json(); });
  }

  function wire() {
    Array.prototype.forEach.call(bodyEl.querySelectorAll('[data-rs]'), function (b) {
      b.onclick = function () {
        var id = b.getAttribute('data-rs'); var n = who(); if (!n) return;
        b.disabled = true; b.textContent = 'Asking the service…';
        post('resync', { recordIds:[id], by:n }).then(function (j) {
          b.textContent = j.mode === 'immediate'
            ? (j.found ? 'Found something — see below' : 'Looked, found nothing')
            : 'Queued — the service will run it';
          var msg = document.createElement('div');
          msg.className = j.found ? 'srt-ok' : 'srt-warn';
          msg.innerHTML = esc(j.message || '') ;
          b.parentNode.appendChild(msg);
          loadState(true).then(function(){ if (view==='record') render(); });
        }).catch(function () { b.disabled = false; b.textContent = 'Service unreachable — try again'; });
      };
    });
    var ra = bodyEl.querySelector('#srt-runall');
    if (ra) ra.onclick = function () {
      var n = who(); if (!n) return;
      ra.disabled = true; ra.textContent = 'Queueing…';
      post('resync', { filter:{}, by:n }).then(function (j) {
        ra.textContent = 'Queued'; var m = document.createElement('div'); m.className='srt-warn';
        m.textContent = j.message || 'Queued.'; ra.parentNode.appendChild(m);
      });
    };
    Array.prototype.forEach.call(bodyEl.querySelectorAll('[data-open]'), function (b) {
      b.onclick = function () { open('record', b.getAttribute('data-open')); };
    });
    var mg = bodyEl.querySelector('#srt-migrate');
    if (mg) mg.onclick = function () {
      var out = bodyEl.querySelector('#srt-migrate-out');
      var migrationGeneration = getSessionGeneration();
      if (!srTok()) { out.innerHTML = '<div class="srt-warn">You are not signed in on this tab, so nothing can be written.</div>'; return; }
      var ids = Object.keys(SEED);
      if (!ids.length) { out.innerHTML = '<div class="srt-warn">Provenance has not loaded.</div>'; return; }
      mg.disabled = true;
      var okCount = 0, failCount = 0, consecutiveFails = 0, done = 0, failures = [];
      out.innerHTML = '<div class="srt-mut" style="margin-top:6px">Writing 0 of ' + ids.length + '\u2026</div>';
      function step(i) {
        if (!active(migrationGeneration)) return;
        if (i >= ids.length || consecutiveFails >= 3) {
          mg.disabled = false;
          mg.textContent = 'Write the 61 grades into HubSpot';
          out.innerHTML = '<div class="' + (failCount ? 'srt-warn' : 'srt-ok') + '" style="margin-top:6px">' +
            '<b>' + okCount + ' written, ' + failCount + ' failed</b> of ' + ids.length + '.' +
            (consecutiveFails >= 3 ? ' <b>Stopped after 3 failures in a row</b> rather than pushing through.' : '') +
            (failures.length ? '<div class="srt-mut" style="margin-top:5px">First failure: ' + esc(failures[0]) + '</div>' : '') +
            (okCount ? '<div class="srt-mut" style="margin-top:5px">Re-run is safe \u2014 it writes the same values again.</div>' : '') +
            '</div>';
          return;
        }
        var id = ids[i], sd = SEED[id];
        var body = { id: String(id),
          champion_confidence: sd.tier || '',
          champion_source_url: sd.src || '',
          champion_detected_at: sd.at || '',
          champion_run_id: sd.run || 'sr-cc-provenance-2026-08-20',
          champion_locked: 'false' };
        fetch('/api/champion-update', { method: 'POST',
          headers: { 'content-type': 'application/json', authorization: 'Bearer ' + srTok() },
          body: JSON.stringify(body) })
          .then(function (r) { return r.json().catch(function () { return {}; })
            .then(function (j) {
              if (r.ok && j.ok) { okCount++; consecutiveFails = 0; }
              else { failCount++; consecutiveFails++;
                     failures.push(id + ': ' + (j.detail || j.error || ('status ' + r.status))); }
            }); })
          .catch(function (e) { failCount++; consecutiveFails++; failures.push(id + ': ' + e.message); })
          .then(function () {
            if (!active(migrationGeneration)) return;
            done++;
            out.innerHTML = '<div class="srt-mut" style="margin-top:6px">Writing ' + done + ' of ' + ids.length +
              ' \u2014 ' + okCount + ' ok, ' + failCount + ' failed\u2026</div>';
            setTimeout(function () { step(i + 1); }, 60);
          });
      }
      step(0);
    };
    var mr = bodyEl.querySelector('#srt-more');
    if (mr) mr.onclick = function () { S.wlLimit = (S.wlLimit || 60) + 60; render(); };
    var rf = bodyEl.querySelector('#srt-refresh');
    if (rf) rf.onclick = function () { rf.textContent='Refreshing…'; loadState(true).then(render); };

    Array.prototype.forEach.call(bodyEl.querySelectorAll('[data-flag]'), function (b) {
      b.onclick = function () {
        var id = b.getAttribute('data-flag'); var n = who(); if (!n) return;
        var why = prompt('What is wrong with it? (optional — "I do not know the right answer" is fine)', '');
        post('flag', { recordId:id, by:n, reason:why||'', storedValue:(infoFor(id).champ||'') })
          .then(function (j) { alert(j.message || 'Flagged.'); loadState(true).then(function(){ render(); }); });
      };
    });

    Array.prototype.forEach.call(bodyEl.querySelectorAll('[data-find]'), function (b) {
      b.onclick = function () {
        var id = b.getAttribute('data-find'); var n = who(); if (!n) return;
        /* In the record view there is a dedicated slot; from the worklist there is not,
           so give the row its own slot — a button that says "Searching…" forever is
           exactly the kind of dead end this feature exists to remove. */
        var host = bodyEl.querySelector('#srt-find');
        if (!host || !b.closest('#srt-find') && b.closest('.srt-row') && !b.closest('.srt-row').contains(host)) {
          var own = b.parentNode.querySelector('.srt-findslot');
          if (!own) { own = document.createElement('div'); own.className = 'srt-findslot'; b.parentNode.appendChild(own); }
          host = own;
        }
        b.disabled = true; b.textContent = 'Searching\u2026';
        if (host) host.innerHTML = '<div class="srt-mut" style="margin-top:7px">Asking the detection service. ' +
          'This runs on the server \u2014 you can close this and come back.</div>';
        post('resync', { recordIds: [id], by: n }).then(function (j) {
          return loadState(false).then(function () { return j; });   // cheap read; the service already patched state
        }).then(function (j) {
          var found = (S.state && S.state.proposals && S.state.proposals[id]) || null;
          if (found) {
            b.disabled = false; b.textContent = 'Found \u2014 review it';
            if (host) host.innerHTML = '<div class="srt-ok" style="margin-top:7px"><b>Found: ' + esc(found.champion) + '</b>' +
              (found.source_url ? ' &middot; <a href="' + esc(found.source_url) + '" target="_blank" rel="noopener" style="color:#86efac">source</a>' : '') +
              '<div style="margin-top:5px">Open it to accept, edit or reject.</div>' +
              '<button class="srt-btn pri" data-open="' + id + '" style="margin-top:6px">Review it</button></div>';
            wire();
            return;
          }
          b.disabled = false; b.textContent = 'Look again';
          if (host) host.innerHTML =
            '<div class="srt-warn" style="margin-top:7px"><b>Nothing found.</b> The detector could not find a ' +
            'published result it trusts for this one. That is a real answer, not an error \u2014 most events ' +
            'outside the big pro and NCAA feeds are not published anywhere it can read.' +
            '<div style="margin-top:6px">If you know who won, set it yourself. It locks, it gets your name on it, ' +
            'and no automation will ever change it.</div>' +
            '<button class="srt-btn pri" data-set="' + id + '" style="margin-top:7px">Set it myself</button></div>';
          wire();
        }).catch(function () {
          b.disabled = false; b.textContent = 'Try again';
          if (host) host.innerHTML = '<div class="srt-warn" style="margin-top:7px">Could not reach the ' +
            'detection service. Nothing was changed.</div>';
        });
      };
    });

    Array.prototype.forEach.call(bodyEl.querySelectorAll('[data-accept]'), function (b) {
      b.onclick = function () {
        var id = b.getAttribute('data-accept'); var n = who(); if (!n) return;
        var p = (S.state.proposals || {})[id]; if (!p) return;
        b.disabled = true; b.textContent = 'Saving\u2026';
        var doNative = nativeWriteAvailable() ? nativeSetChampion(id, p.champion)
                                              : Promise.resolve({ ok: false, reason: 'not on the platform' });
        doNative.then(function (nat) {
          return post('override', { recordId: id, champion: p.champion, by: n,
            source_url: p.source_url || '', note: 'Accepted the suggested champion after checking the source.',
            previousValue: null, nativeWrite: nat, acceptedProposal: true, proposalTier: p.tier });
        }).then(function () { return loadState(true); })
          .then(function () { repaint(); render(); })
          .catch(function () { b.disabled = false; b.textContent = 'Try again'; });
      };
    });

    Array.prototype.forEach.call(bodyEl.querySelectorAll('[data-reject]'), function (b) {
      b.onclick = function () {
        var id = b.getAttribute('data-reject'); var n = who(); if (!n) return;
        var why = prompt('What is wrong with the suggestion? (optional)', '') || '';
        b.disabled = true; b.textContent = 'Rejecting\u2026';
        post('reject', { recordId: id, by: n, reason: why })
          .then(function () { return loadState(true); })
          .then(function () { render(); });
      };
    });

    Array.prototype.forEach.call(bodyEl.querySelectorAll('[data-clear]'), function (b) {
      b.onclick = function () {
        var id = b.getAttribute('data-clear'); var n = who(); if (!n) return;
        if (!confirm('Remove the override on this record? It will go back to whatever the source data says.')) return;
        post('override', { recordId: id, by: n, clear: true }).then(function (j) {
          alert(j.message || 'Removed.'); loadState(true).then(function () { repaint(); render(); });
        });
      };
    });
    Array.prototype.forEach.call(bodyEl.querySelectorAll('[data-set]'), function (b) {
      b.onclick = function () {
        ctxId = b.getAttribute('data-set'); view = 'record'; render();
        var host = bodyEl.querySelector('#srt-form'); if (!host) return;
        var cur = infoFor(ctxId);
        host.innerHTML = '<div class="srt-row"><b>Set the champion</b>' +
          '<div class="srt-mut">This becomes the record. It locks, and no automation will ever change it.</div>' +
          '<input class="srt-in" id="srt-ch" placeholder="Champion (school, team or person)" value="' + esc(cur.champ||'') + '">' +
          '<input class="srt-in" id="srt-su" placeholder="Link to proof, if you have one (optional)">' +
          '<input class="srt-in" id="srt-nt" placeholder="How do you know? e.g. I was there / their AD told me / saw it on the state site">' +
          '<input class="srt-in" id="srt-by" placeholder="Your name" value="' + esc(S.me||'') + '">' +
          '<button class="srt-btn pri" id="srt-save">Save and lock</button>' +
          '<button class="srt-btn" id="srt-cancel">Cancel</button><div id="srt-res"></div></div>';
        host.querySelector('#srt-cancel').onclick = function () { host.innerHTML=''; };
        host.querySelector('#srt-save').onclick = function () {
          var ch = host.querySelector('#srt-ch').value.trim();
          var by = host.querySelector('#srt-by').value.trim();
          if (!ch || !by) { host.querySelector('#srt-res').innerHTML = '<div class="srt-warn">Champion and your name are both required.</div>'; return; }
          S.me = by; localStorage.setItem('sr_truth_me', by);
          this.disabled = true; this.textContent = 'Saving…';
          var doNative = nativeWriteAvailable() ? nativeSetChampion(ctxId, ch)
                                               : Promise.resolve({ ok: false, reason: 'not on the platform' });
          doNative.then(function (nat) {
          post('override', { recordId: ctxId, champion: ch, by: by,
            source_url: host.querySelector('#srt-su').value.trim(),
            note: host.querySelector('#srt-nt').value.trim(),
            previousValue: cur.champ || null, nativeWrite: nat })
            .then(function (j) {
              if (nat.ok) {
                host.querySelector('#srt-res').innerHTML = '<div class="srt-ok">' + esc(j.message || 'Saved.') +
                  '<br><b>Written into HubSpot</b> as ' + esc(nat.by || by) + '. The champion on the record is now yours.</div>';
              } else {
                host.querySelector('#srt-res').innerHTML =
                  '<div class="srt-warn"><b>NOT saved to HubSpot.</b> ' + esc(nat.reason || 'The write did not go through.') +
                  '<div style="margin-top:5px">It is held here under your name so you have not lost it, ' +
                  'but <b>the record in HubSpot is unchanged</b>' +
                  (nat.kind === 'no-session' || nat.kind === 'expired'
                    ? ' — sign in and set it again.' : ' — tell Brian if this keeps happening.') + '</div></div>';
              }
              loadState(true).then(function () { repaint(); render(); });
            }).catch(function(){ host.querySelector('#srt-res').innerHTML='<div class="srt-warn">Could not reach the service. Nothing was saved — try again.</div>'; });
          });
        };
      };
    });
  }

  /* ---------- 6. go ---------- */
  /* ---------- 6. AUTH GATE ----------
     This script is injected into the HTML shell, so it also loads on the signed-out
     login page. Until there is a Southern Recognition session it must do NOTHING:
     no globals, no styles, no DOM, no network, and above all no provenance data in
     page memory. Provenance is fetched from the service, authenticated, after login. */
  function start() {
    /* The ownership flag is checked HERE as well as at module init, and that is
       not belt-and-braces. Both copies load on the SIGNED-OUT login page, where
       nothing has started yet, so both get past their init guard and both arm a
       login watcher. At sign-in they would then both run: `started` is
       module-local and cannot see the other copy. Checking a shared window flag
       at the moment of starting is the only thing that makes "one copy wins"
       true rather than aspirational. */
    if (started || !srTok()) return;
    started = true; activeGeneration = getSessionGeneration();
    var ticket = activeGeneration;
    /* Claim ownership on the flag only this module writes, and ALSO keep setting
       the shared flag. Setting both is not redundancy for its own sake: if the
       inline head claim in index.html is ever lost — an edit, a different shell,
       a stale cached index.html served to one phone — the shared flag is the
       original mutual exclusion and still stops the snippet double-running on
       that page. The honest failure there is "the stale copy wins again", which
       the foreign-origin guard in verify-live.mjs reports out loud; it is not
       "two copies paint the same rows". */
    window.__SR_TRUTH_OWNER__ = 'repo-bundle';
    window.__SR_TRUTH__ = true;
    if (!hooksInstalled) { installHooks(); installStyles(); hooksInstalled = true; }
    installDebug(); installRepaint();
    window.__srtOpen = function (id) { open(id ? 'record' : 'summary', id || null); };
    loadProvenance()
      .then(function () { if (!active(ticket)) throw sessionChangedError(); return loadState(false); })
      .then(function () { if (active(ticket)) paint(); })
      .catch(function (e) { if (!active(ticket)) return; SEED = {}; S.provenanceError = provenanceFailure(e.status); if (panel) render(); });
  }
  onSessionGenerationChange(function () {
    teardown('signed-in session changed');
    if (srTok()) start();
  });
  function gate() { if (srTok()) start(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', gate); else gate();
}
